import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, EstadoVacio, Seccion } from '@/components/Card';
import { Falta } from '@/components/Falta';
import { estaDescartado } from '@/domain/planning';
import { NumberField, SelectField, TextField } from '@/components/Field';
import {
  agregarPlatillo,
  editarPlatillo,
  quitarPlatillo,
  descartarPlatillo,
  reactivarPlatillo,
  verMenuEntre,
  type RenglonMenu,
} from '@/db/repos/menu';
import { recetasParaMenu } from '@/db/repos/production';
import { pedidosPorRecetaEntre, type PedidoDeReceta } from '@/db/repos/orders';
import { AvisoDesajuste } from '@/components/AvisoDesajuste';
import {
  hoy,
  sumarDias,
  diasDeSemana,
  nombreDia,
  formatearCorto,
  formatearMedio,
  esHoy,
  type FechaLocal,
} from '@/domain/dates';
import { formatearCentavos } from '@/domain/money';
import { mensajeDeError } from '@/db/tx';

type Vista =
  | { modo: 'semana' }
  | { modo: 'agregar'; fecha: FechaLocal }
  | { modo: 'editar'; renglon: RenglonMenu };

/**
 * Menu semanal: que se vende cada dia y cuantas porciones.
 *
 * Cada platillo muestra tambien CUANDO hay que cocinarlo, que es el dato que no
 * existia antes de esta fase. Sin el, el menu dice que vender pero no cuando
 * ponerse a trabajar.
 */
export function MenuPage() {
  const [ancla, setAncla] = useState<FechaLocal>(hoy());
  const [vista, setVista] = useState<Vista>({ modo: 'semana' });

  const dias = diasDeSemana(ancla);
  const desde = dias[0] ?? ancla;
  const hasta = dias[6] ?? ancla;

  const menu = useLiveQuery(() => verMenuEntre(desde, hasta), [desde, hasta]);

  /**
   * LO QUE DE VERDAD ESTA PEDIDO.
   *
   * El menu guarda un ESTIMADO, y esta pantalla mostraba solo ese numero. Leido
   * de corrido, "4 porciones" se entiende como cuatro personas esperando, cuando
   * puede no haber ninguna. Produccion y Tablero ya mandaban lo pedido; el menu
   * se habia quedado atras y contradecia a las otras dos.
   *
   * No hace falta el margen de dias que usa Produccion: aqui los platillos ya
   * vienen filtrados por fecha de VENTA, que es la misma con la que se agrupan
   * los pedidos.
   */
  const pedidos = useLiveQuery(() => pedidosPorRecetaEntre(desde, hasta), [desde, hasta]);
  const pedidoDe = (renglon: RenglonMenu): PedidoDeReceta | undefined =>
    pedidos?.get(`${renglon.platillo.fechaVenta}|${renglon.platillo.recipeId}`);

  if (vista.modo === 'agregar') {
    return (
      <AgregarPlatilloForm
        fecha={vista.fecha}
        onListo={() => setVista({ modo: 'semana' })}
        onCancelar={() => setVista({ modo: 'semana' })}
      />
    );
  }

  if (vista.modo === 'editar') {
    return (
      <EditarPlatilloForm
        renglon={vista.renglon}
        onListo={() => setVista({ modo: 'semana' })}
        onCancelar={() => setVista({ modo: 'semana' })}
      />
    );
  }

  const ventaEsperada = (menu ?? []).reduce((s, r) => s + r.ventaEsperadaCentavos, 0);

  /**
   * Lo pedido de los platillos que estan en el menu de la semana.
   *
   * Se suma renglon por renglon, y no con el resumen general de pedidos, para que
   * el total de arriba corresponda exactamente a lo que se ve abajo.
   */
  const conPedido = (menu ?? [])
    .map((r) => pedidoDe(r))
    .filter((p): p is PedidoDeReceta => p !== undefined);
  const totalPedido = conPedido.reduce((s, p) => s + p.totalCentavos, 0);
  const porcionesPedidas = conPedido.reduce((s, p) => s + p.porciones, 0);

  return (
    <div className="space-y-5">
      <PageHeader titulo="Menú semanal" descripcion="Qué se vende cada día y cuántas porciones" />

      <div className="flex items-center gap-2">
        <Button variante="secundario" completo={false} onClick={() => setAncla(sumarDias(ancla, -7))}>
          ‹ Anterior
        </Button>
        <div className="flex-1 text-center">
          <p className="text-sm font-medium text-texto">
            {formatearCorto(desde)} – {formatearCorto(hasta)}
          </p>
        </div>
        <Button variante="secundario" completo={false} onClick={() => setAncla(sumarDias(ancla, 7))}>
          Siguiente ›
        </Button>
      </div>

      {ancla !== hoy() && (
        <Button variante="fantasma" onClick={() => setAncla(hoy())}>
          Volver a esta semana
        </Button>
      )}

      {menu !== undefined && menu.length > 0 && (
        <Card className="bg-superficie-suave">
          {/*
            Primero lo pedido, que es dinero comprometido, y despues el techo del
            estimado. Al reves invita a contar como ingreso algo que nadie aparto.
          */}
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-texto-suave">
              Pedido esta semana
              {porcionesPedidas > 0 && (
                <span className="text-texto-tenue">
                  {' · '}
                  {porcionesPedidas}{' '}
                  {porcionesPedidas === 1 ? 'porción' : 'porciones'}
                </span>
              )}
            </span>
            <span className="text-xl font-bold tabular-nums text-marca-fuerte">
              {formatearCentavos(totalPedido)}
            </span>
          </div>
          <div className="mt-1 flex items-baseline justify-between gap-3">
            <span className="text-xs text-texto-tenue">Si se vendiera todo el estimado</span>
            <span className="text-sm tabular-nums text-texto-tenue">
              {formatearCentavos(ventaEsperada)}
            </span>
          </div>
        </Card>
      )}

      {menu === undefined && <p className="text-sm text-texto-suave">Cargando…</p>}

      {menu !== undefined && (
        <ul className="space-y-4">
          {dias.map((dia) => {
            const delDia = menu.filter((r) => r.platillo.fechaVenta === dia);

            return (
              <li key={dia}>
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <h2
                    className={`text-sm font-semibold capitalize ${
                      esHoy(dia) ? 'text-marca-fuerte' : 'text-texto'
                    }`}
                  >
                    {nombreDia(dia)} {formatearCorto(dia)}
                    {esHoy(dia) && <span className="ml-2 text-xs font-normal">(hoy)</span>}
                  </h2>
                </div>

                {delDia.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => setVista({ modo: 'agregar', fecha: dia })}
                    className="min-h-11 w-full rounded-xl border border-dashed border-borde-fuerte bg-superficie-suave px-3 text-sm text-texto-suave active:bg-superficie"
                  >
                    + Agregar platillo
                  </button>
                ) : (
                  <div className="space-y-2">
                    {delDia.map((renglon) => (
                      <Card
                        key={renglon.platillo.id}
                        className={
                          estaDescartado(renglon.platillo)
                            ? 'border-dashed bg-superficie-suave'
                            : ''
                        }
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p
                              className={`font-semibold ${
                                estaDescartado(renglon.platillo)
                                  ? 'text-texto-tenue line-through'
                                  : 'text-texto'
                              }`}
                            >
                              {renglon.nombreReceta}
                            </p>
                            {estaDescartado(renglon.platillo) && (
                              <p className="text-xs font-medium text-texto-tenue">
                                No se cocinó
                              </p>
                            )}
                            {/* Manda lo pedido; el estimado pasa a segundo plano. */}
                            <PedidasDeVerdad
                              estimado={renglon.platillo.porcionesPlaneadas}
                              pedido={pedidoDe(renglon)}
                              precioCentavos={renglon.platillo.precioVentaCentavos}
                            />
                            <AvisoDesajuste
                              cocinadas={renglon.porcionesCocinadas}
                              pedido={pedidoDe(renglon)}
                            />
                            <p className="mt-1 text-xs text-texto-tenue">
                              {renglon.platillo.diasAnticipacion === 0
                                ? 'Se cocina el mismo día'
                                : `Cocinar el ${formatearMedio(renglon.platillo.fechaPrep)}`}
                            </p>
                            {renglon.platillo.notas !== null && (
                              <p className="mt-1 text-xs italic text-texto-suave">
                                {renglon.platillo.notas}
                              </p>
                            )}
                          </div>

                          <div className="shrink-0 text-right">
                            {/*
                              Con pedidos se muestra lo que se va a cobrar de
                              verdad, tomado del total de cada pedido. Sin pedidos
                              queda el estimado, en gris para que no se confunda
                              con dinero comprometido.
                            */}
                            {(() => {
                              const pedido = pedidoDe(renglon);
                              const hayPedidos = pedido !== undefined && pedido.cuantos > 0;
                              return (
                                <>
                                  <p
                                    className={`font-semibold tabular-nums ${
                                      hayPedidos ? 'text-texto' : 'text-texto-tenue'
                                    }`}
                                  >
                                    {formatearCentavos(
                                      hayPedidos
                                        ? pedido.totalCentavos
                                        : renglon.ventaEsperadaCentavos,
                                    )}
                                  </p>
                                  {!hayPedidos && (
                                    <p className="text-[11px] leading-tight text-texto-tenue">
                                      estimado
                                    </p>
                                  )}
                                </>
                              );
                            })()}
                            <EstadoSello estado={renglon.estadoProduccion} />
                          </div>
                        </div>

                        <div className="mt-3 flex gap-2">
                          <Button
                            variante="fantasma"
                            completo={false}
                            onClick={() => setVista({ modo: 'editar', renglon })}
                          >
                            Editar
                          </Button>
                        </div>
                      </Card>
                    ))}

                    <button
                      type="button"
                      onClick={() => setVista({ modo: 'agregar', fecha: dia })}
                      className="min-h-11 w-full rounded-xl border border-dashed border-borde-fuerte px-3 text-sm text-texto-suave active:bg-superficie-suave"
                    >
                      + Agregar otro
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Cuantas porciones estan PEDIDAS de verdad, frente a las estimadas.
 *
 * Es el mismo criterio que la pantalla de Produccion, a proposito: dos pantallas
 * que hablan de la misma comida no pueden dar numeros distintos. Aqui se agrega
 * el precio por porcion, que en el menu si hace falta para editar el platillo.
 */
function PedidasDeVerdad({
  estimado,
  pedido,
  precioCentavos,
}: {
  estimado: number;
  pedido: PedidoDeReceta | undefined;
  precioCentavos: number;
}) {
  if (pedido === undefined || pedido.cuantos === 0) {
    return (
      <p className="text-sm text-texto-tenue">
        Nadie ha pedido todavía · el menú estima {estimado} ·{' '}
        {formatearCentavos(precioCentavos)} cada una
      </p>
    );
  }

  return (
    <div>
      <p className="text-sm font-semibold text-marca-fuerte">
        {pedido.porciones} {pedido.porciones === 1 ? 'porción pedida' : 'porciones pedidas'} por{' '}
        {pedido.cuantos} {pedido.cuantos === 1 ? 'persona' : 'personas'}
      </p>
      <p className="text-sm text-texto-suave">
        {pedido.clientes.map((c) => `${c.clienteNombre} ${c.porciones}`).join(', ')}
      </p>
      <p className="mt-0.5 text-xs text-texto-tenue">
        el menú estima {estimado} · {formatearCentavos(precioCentavos)} cada una
      </p>
    </div>
  );
}

function EstadoSello({ estado }: { estado: RenglonMenu['estadoProduccion'] }) {
  if (estado === 'completada') {
    return <span className="text-xs font-medium text-exito">✓ cocinado</span>;
  }
  if (estado === 'cancelada') {
    return <span className="text-xs text-texto-tenue">revertido</span>;
  }
  return <span className="text-xs text-texto-tenue">por cocinar</span>;
}

// ---------------------------------------------------------------------------

interface PropsAgregar {
  fecha: FechaLocal;
  onListo: () => void;
  onCancelar: () => void;
}

function AgregarPlatilloForm({ fecha, onListo, onCancelar }: PropsAgregar) {
  const recetas = useLiveQuery(() => recetasParaMenu(), []);
  const [recipeId, setRecipeId] = useState('');
  const [porciones, setPorciones] = useState('');
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const elegida = recetas?.find((r) => r.id === recipeId);
  const porcionesNumero = Number(porciones);

  // Lista de faltantes y no solo un booleano, para poder decir que falta debajo
  // del boton. Un boton apagado sin motivo no da ninguna pista.
  const faltan: string[] = [];
  if (recipeId === '') faltan.push('el platillo');
  if (!Number.isFinite(porcionesNumero) || porcionesNumero <= 0) faltan.push('las porciones');
  const valida = faltan.length === 0;

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      await agregarPlatillo({
        fechaVenta: fecha,
        recipeId,
        porcionesPlaneadas: porcionesNumero,
        notas: notas.trim() === '' ? null : notas,
      });
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  if (recetas !== undefined && recetas.length === 0) {
    return (
      <div className="space-y-5">
        <PageHeader titulo="Agregar platillo" />
        <EstadoVacio
          titulo="Todavía no hay recetas"
          descripcion="Crea una receta primero para poder ponerla en el menú."
        />
        <Button variante="secundario" onClick={onCancelar}>
          Volver
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader titulo="Agregar platillo" descripcion={`Se vende el ${formatearMedio(fecha)}`} />

      <SelectField
        etiqueta="Platillo"
        valor={recipeId}
        opciones={[
          { valor: '', etiqueta: 'Elige una receta…' },
          ...(recetas ?? []).map((r) => ({ valor: r.id, etiqueta: r.nombre })),
        ]}
        onCambio={(valor) => {
          setRecipeId(valor);
          const receta = recetas?.find((r) => r.id === valor);
          if (receta !== undefined && porciones === '') {
            setPorciones(String(receta.rendimientoPorciones));
          }
        }}
      />

      <NumberField
        etiqueta="Cuántas porciones"
        valor={porciones}
        onCambio={setPorciones}
        placeholder="5"
        decimal={false}
        ayuda={
          elegida === undefined
            ? undefined
            : `La receta rinde ${elegida.rendimientoPorciones}. Puedes poner más o menos.`
        }
      />

      {elegida !== undefined && (
        <Banner
          tono="info"
          titulo={
            elegida.diasAnticipacion === 0
              ? 'Se cocina el mismo día'
              : `Hay que cocinarlo el ${formatearMedio(
                  sumarDias(fecha, -elegida.diasAnticipacion),
                )}`
          }
        >
          {elegida.diasAnticipacion === 0
            ? 'Esta receta está marcada para prepararse el mismo día que se vende.'
            : `Son ${elegida.diasAnticipacion} ${
                elegida.diasAnticipacion === 1 ? 'día' : 'días'
              } de anticipación. Lo puedes cambiar después platillo por platillo.`}
        </Banner>
      )}

      {valida && elegida !== undefined && (
        <Card className="bg-marca-suave">
          <p className="text-center text-sm text-texto-suave">Venta esperada</p>
          <p className="text-center text-2xl font-bold tabular-nums text-marca-fuerte">
            {formatearCentavos(porcionesNumero * elegida.precioVentaCentavos)}
          </p>
        </Card>
      )}

      <TextField
        etiqueta="Nota"
        valor={notas}
        onCambio={setNotas}
        placeholder="Opcional: pedido de la vecina…"
        maxLength={200}
      />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo agregar">
          {error}
        </Banner>
      )}

      <div className="space-y-2">
        <Button tamano="grande" disabled={guardando || !valida} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : 'Agregar al menú'}
        </Button>
        <Falta faltan={faltan} />
        <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsEditar {
  renglon: RenglonMenu;
  onListo: () => void;
  onCancelar: () => void;
}

function EditarPlatilloForm({ renglon, onListo, onCancelar }: PropsEditar) {
  const [porciones, setPorciones] = useState(String(renglon.platillo.porcionesPlaneadas));
  const [dias, setDias] = useState(String(renglon.platillo.diasAnticipacion));
  const [notas, setNotas] = useState(renglon.platillo.notas ?? '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const porcionesNumero = Number(porciones);
  const diasNumero = Number(dias);

  const faltan: string[] = [];
  if (!Number.isFinite(porcionesNumero) || porcionesNumero <= 0) faltan.push('las porciones');
  if (!Number.isInteger(diasNumero) || diasNumero < 0) {
    faltan.push('los días de anticipación');
  }
  const valida = faltan.length === 0;

  const nuevaPrep = valida ? sumarDias(renglon.platillo.fechaVenta, -diasNumero) : null;

  async function guardar() {
    setGuardando(true);
    setError(null);
    try {
      await editarPlatillo(renglon.platillo.id, {
        porcionesPlaneadas: porcionesNumero,
        diasAnticipacion: diasNumero,
        notas: notas.trim() === '' ? null : notas,
      });
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  async function quitar() {
    setGuardando(true);
    setError(null);
    try {
      await quitarPlatillo(renglon.platillo.id);
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  const descartado = estaDescartado(renglon.platillo);

  async function descartar() {
    setGuardando(true);
    setError(null);
    try {
      await descartarPlatillo(renglon.platillo.id, 'No se cocinó: no hubo pedidos.');
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  async function volverAPoner() {
    setGuardando(true);
    setError(null);
    try {
      await reactivarPlatillo(renglon.platillo.id);
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        titulo={renglon.nombreReceta}
        descripcion={`Se vende el ${formatearMedio(renglon.platillo.fechaVenta)}`}
      />

      {renglon.estadoProduccion === 'completada' && (
        <Banner tono="aviso" titulo="Ya está cocinado">
          Este platillo ya se produjo y su costo quedó registrado. Puedes cambiar las porciones,
          pero el inventario ya se descontó con la cantidad anterior.
        </Banner>
      )}

      <NumberField
        etiqueta="Cuántas porciones"
        valor={porciones}
        onCambio={setPorciones}
        decimal={false}
      />

      <NumberField
        etiqueta="Días de anticipación"
        valor={dias}
        onCambio={setDias}
        decimal={false}
        ayuda="Cero si se cocina el mismo día que se vende"
      />

      {nuevaPrep !== null && (
        <Card className="bg-superficie-suave">
          <p className="text-sm text-texto-suave">Se tendría que cocinar el</p>
          <p className="text-base font-semibold capitalize text-texto">
            {formatearMedio(nuevaPrep)}
          </p>
        </Card>
      )}

      <TextField etiqueta="Nota" valor={notas} onCambio={setNotas} maxLength={200} />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo completar">
          {error}
        </Banner>
      )}

      <div className="space-y-2">
        <Button tamano="grande" disabled={guardando || !valida} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : 'Guardar cambios'}
        </Button>
        <Falta faltan={faltan} />
        <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
          Cancelar
        </Button>
      </div>

      {/*
        Dos salidas distintas para un platillo que no se cocino:

        "No lo cociné" conserva el registro de que ese día se planeó y no hubo
        pedidos, que es el dato útil al armar la semana siguiente. Solo lo saca
        del tablero y de producción.

        "Quitar del menú" lo borra y con él ese dato. Se reserva para el error
        de captura.
      */}
      {renglon.estadoProduccion !== 'completada' && (
        <Seccion
          titulo="Este platillo no se cocinó"
          descripcion="Quítalo del tablero sin perder el registro de que ese día no hubo pedidos."
        >
          {descartado ? (
            <Button
              variante="secundario"
              disabled={guardando}
              onClick={() => void volverAPoner()}
            >
              Sí lo cociné, ponlo de vuelta
            </Button>
          ) : (
            <Button variante="secundario" disabled={guardando} onClick={() => void descartar()}>
              No lo cociné, no hubo pedidos
            </Button>
          )}

          <Button variante="fantasma" disabled={guardando} onClick={() => void quitar()}>
            Lo capturé por error, quitar del menú
          </Button>
        </Seccion>
      )}

      {renglon.estadoProduccion === 'completada' && (
        <Button variante="fantasma" disabled={guardando} onClick={() => void quitar()}>
          Quitar del menú
        </Button>
      )}
    </div>
  );
}
